import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import Button, { ButtonTypes } from 'components/common/button';
import Chip from 'components/common/chip';
import ComposerContext from 'components/composer/contexts/composer_context';
import connect from 'components/lib/connect';
import { ConversationTopicsBaseV2 } from 'components/customer/conversation_history/topics_v2/conversation_topics_v2';
import { customAttributeValuesFromDto } from 'models/custom_attribute';
import { TopicsMenuOptionType } from 'components/customer/conversation_history/topics_v2/conversation_topics_constants_v2';
import UpdateComposition from 'actions/composer/update_composition';
import { useExecuteAction } from 'components/hooks/connect_hooks';

const noop = () => {};

// Topic / custom-attribute picker for the task composer header. Mirrors
// TaskTopicsPills in the feed, but writes to the draft composition rather than
// dispatching task actions — there's no task yet. AddTask carries the ids over.
export function TaskTopicsSelector({
  className,
  customAttributesImmutable,
  customAttributesProvider,
  topicsImmutable,
  topicsProvider,
}) {
  const { t } = useTranslation();
  const executeAction = useExecuteAction();
  const { composition } = useContext(ComposerContext);
  const [openRequestedAt, setOpenRequestedAt] = useState();

  const compositionId = composition.id;
  const customerId = composition.customerId;
  // Memoized because a fresh [] each render defeats the multi-select's PureComponent.
  const topicIds = useMemo(() => composition.content.topicIds || [], [composition.content.topicIds]);
  const customAttributes = useMemo(
    () => composition.content.customAttributes || [],
    [composition.content.customAttributes]
  );

  const getTopic = useCallback(id => topicsProvider.findBy({ id }), [topicsProvider]);

  const updateContent = useCallback(
    attrs => executeAction(UpdateComposition, { compositionId, customerId, attrs }),
    [compositionId, customerId, executeAction]
  );

  const appliedTopics = useMemo(
    () =>
      _.compact(
        topicIds.map(id => {
          const topic = getTopic(id);
          return topic && { id, name: topic.getNameWithAncestry() };
        })
      ),
    // topicsImmutable recomputes on rename/archive, which don't change topicIds.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [topicIds, topicsImmutable, getTopic]
  );

  const appliedAttributes = useMemo(
    () =>
      customAttributes.map(attr => {
        const definition = customAttributesProvider.findBy({ id: attr.id });
        return {
          id: attr.id,
          value: attr.value,
          label: definition ? definition.label : attr.id,
        };
      }),
    // customAttributesImmutable recomputes on rename, which doesn't change customAttributes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [customAttributes, customAttributesImmutable, customAttributesProvider]
  );

  const removeTopic = useCallback(
    id => updateContent({ topicIds: _.without(topicIds, id) }),
    [topicIds, updateContent]
  );

  const removeAttribute = useCallback(
    attr =>
      updateContent({
        customAttributes: customAttributes.filter(({ id, value }) => !(id === attr.id && value === attr.value)),
      }),
    [customAttributes, updateContent]
  );

  const onTopicChange = useCallback(
    updated => {
      const updatedTopicIds = _.filter(updated.optionIds, id => getTopic(id));
      // The menu emits plain {id, value}; the content model types these as
      // [CustomAttributeValue] and throws on anything else, losing the attribute.
      const updatedAttributes = customAttributeValuesFromDto(updated.attributeValues || []);

      const attrs = {};
      if (!_.isEqual(updatedTopicIds, topicIds)) {
        attrs.topicIds = updatedTopicIds;
      }
      if (!_.isEqual(updatedAttributes, customAttributes)) {
        attrs.customAttributes = updatedAttributes;
      }
      if (!_.isEmpty(attrs)) {
        updateContent(attrs);
      }
    },
    [customAttributes, getTopic, topicIds, updateContent]
  );

  const openMenu = useCallback(() => setOpenRequestedAt(new Date().toISOString()), []);

  return (
    <Container className={className}>
      <PillRow>
        {appliedTopics.map(topic => (
          <Chip
            key={`topic-${topic.id}`}
            label={topic.name}
            onDelete={() => removeTopic(topic.id)}
            small
            title={topic.name}
          />
        ))}
        {appliedAttributes.map(attr => {
          const label = `${attr.label}: ${attr.value}`;
          return (
            <Chip
              key={`attr-${attr.id}-${attr.value}`}
              label={label}
              onDelete={() => removeAttribute(attr)}
              small
              title={label}
            />
          );
        })}
        <AddTopicLink buttonType={ButtonTypes.TEXT} data-aid="taskComposer-addTopic" onClick={openMenu}>
          {t('Add topic')}
        </AddTopicLink>
      </PillRow>
      <StyledEditor>
        <TaskCompositionTopicsMenu
          customAttributes={customAttributes}
          customAttributesImmutable={customAttributesImmutable}
          customAttributesProvider={customAttributesProvider}
          onTopicChange={onTopicChange}
          openRequestedAt={openRequestedAt}
          topicIds={topicIds}
          topicsImmutable={topicsImmutable}
          topicsProvider={topicsProvider}
        />
      </StyledEditor>
    </Container>
  );
}

TaskTopicsSelector.propTypes = {
  className: PropTypes.string,
  // Observed store bindings; dependencies so options and chips refresh when a
  // definition is renamed, added, or deactivated.
  customAttributesImmutable: PropTypes.object,
  customAttributesProvider: PropTypes.shape({ findAll: PropTypes.func.isRequired, findBy: PropTypes.func.isRequired })
    .isRequired,
  topicsImmutable: PropTypes.object,
  topicsProvider: PropTypes.shape({ findAll: PropTypes.func.isRequired, findBy: PropTypes.func.isRequired }).isRequired,
};

// Separate from the pill row so the option lists memoize on the store bindings
// rather than on every keystroke in the composer.
function TaskCompositionTopicsMenu({
  customAttributes,
  customAttributesImmutable,
  customAttributesProvider,
  onTopicChange,
  openRequestedAt,
  topicIds,
  topicsImmutable,
  topicsProvider,
}) {
  const topics = useMemo(
    () =>
      topicsProvider.findAll().map(topic => ({
        id: topic.id,
        categoryOnly: !!topic.categoryOnly,
        disabled: topic.disabled,
        name: topic.getNameWithAncestry(),
        parentId: topic.parentId,
        type: TopicsMenuOptionType.TOPIC,
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [topicsImmutable, topicsProvider]
  );

  const attributeOptions = useMemo(
    () =>
      _.map(customAttributesProvider.findAll({ select: ['id', 'deactivated', 'label'] }), attr => ({
        id: attr.id,
        disabled: attr.deactivated,
        name: attr.label,
        type: TopicsMenuOptionType.CUSTOM_ATTRIBUTE,
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [customAttributesImmutable, customAttributesProvider]
  );

  // ConversationTopicsBaseV2 is a PureComponent.
  const conversation = useMemo(() => ({ topicIds, customAttributes }), [topicIds, customAttributes]);

  return (
    <ConversationTopicsBaseV2
      conversation={conversation}
      customAttributes={attributeOptions}
      dropDown
      hideSelectedOptions
      onMenuClose={noop}
      onMenuOpen={noop}
      onTopicChange={onTopicChange}
      onTopicSearch={noop}
      onTopicSelect={noop}
      openRequestedAt={openRequestedAt}
      topics={topics}
      usePopover
    />
  );
}

TaskCompositionTopicsMenu.propTypes = {
  customAttributes: PropTypes.array,
  customAttributesImmutable: PropTypes.object,
  customAttributesProvider: PropTypes.shape({ findAll: PropTypes.func.isRequired, findBy: PropTypes.func.isRequired })
    .isRequired,
  onTopicChange: PropTypes.func.isRequired,
  openRequestedAt: PropTypes.string,
  topicIds: PropTypes.array,
  topicsImmutable: PropTypes.object,
  topicsProvider: PropTypes.shape({ findAll: PropTypes.func.isRequired, findBy: PropTypes.func.isRequired }).isRequired,
};

const Container = styled.div`
  display: flex;
  flex: 1;
  min-width: 0;
`;

const PillRow = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: ${p => p.theme.spacing.medium};
`;

// A link rather than a pill, so it reads as an action instead of a value. The
// "+" is decorative and stays out of the translated key.
const AddTopicLink = styled(Button)`
  align-items: center;
  color: ${p => p.theme.colors.green400};
  display: inline-flex;
  font-size: ${p => p.theme.fontSize.reduced};
  font-weight: ${p => p.theme.fontWeight.medium};
  min-height: 24px;
  padding: 0;

  &::before {
    content: '+';
    margin-right: 4px;
  }

  &:hover {
    color: ${p => p.theme.colors.green500};
  }
`;

// The shared control is used purely as the menu behind "+ Add topic": its
// collapsed comma display and chevron are hidden so only the dropdown surfaces.
const StyledEditor = styled.div`
  .conversationTopic-display {
    display: none;
  }

  .conversationTopic-multi::after {
    display: none;
  }

  .conversationTopic-multi {
    height: auto;
    justify-content: flex-start;
    max-width: 100%;
    min-height: 0;
  }

  .conversationTopic-menuOpener {
    padding-left: 0;
  }
`;

function mapStateToProps({ getProvider }) {
  const topicsProvider = getProvider('topics');

  const customAttributesProvider = getProvider('customAttributes');

  return {
    customAttributesProvider,
    customAttributesImmutable: customAttributesProvider.immutableStore.binding.get(),
    topicsProvider,
    topicsImmutable: topicsProvider.immutableStore.binding.get(),
  };
}

export default connect(mapStateToProps)(TaskTopicsSelector);
